import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from "react";
import { createDialogStack } from "./dialog-stack.ts";
import { focused, tabbable } from "./focus.ts";
import { useKitInput } from "./input.tsx";
import { keepLayer } from "./modal-layer.ts";

/**
 * What makes a box a modal (CLIENT.md 7.3 and 7.7; ADR-061 decision 4 as amended), shared by the kit's `Dialog`
 * and `ModalWindow`: while it is open it is the topmost layer of the input router, so Escape asks the page to
 * close it, and it holds the keyboard, so no key reaches the game behind it; Tab and Shift+Tab stay inside it,
 * focus that lands outside it is brought back, and a press on its scrim beside it takes no focus from it. It
 * focuses its first safe control as it opens and gives focus back, as it closes, to what had focus as it opened,
 * read while it first renders, before a control inside it that focuses itself takes focus.
 *
 * Modals stack: one opened while another is open is a later sibling in the same positioned stage. Only the
 * topmost modal on the page keeps focus and Tab inside it; one beneath does neither until it is on top again, and
 * focus that comes back to it, such as the control that opened the modal above, stays where it lands.
 */

/**
 * One open modal, as the page's stack holds it.
 */
interface OpenModal {
  /**
   * Focuses the modal's first safe control, or the modal itself; does nothing once it has left the page.
   */
  readonly focusFirst: () => void;
  /**
   * Whether a node is inside the modal.
   */
  readonly holds: (node: Node | null) => boolean;
}

/**
 * A modal's parts as its first focus reads them: its content, in the order its first safe control is looked for,
 * and its close button, or null when it has none.
 */
export interface ModalParts {
  readonly content: readonly HTMLElement[];
  readonly close: HTMLElement | null;
}

/**
 * The page's open modals, dialogs and modal windows alike.
 */
const OPEN = createDialogStack<OpenModal>();

/**
 * The control a modal focuses as it opens: the first in its content that is not a danger button, or its close
 * button.
 *
 * @param parts The modal's content and its close button.
 * @returns The control to focus, or null when neither exists.
 */
export function firstSafe(parts: ModalParts): HTMLElement | null {
  for (const part of parts.content) {
    const control = tabbable(part).find((element) => !element.hasAttribute("data-ot-danger"));
    if (control !== undefined) {
      return control;
    }
  }
  return parts.close;
}

/**
 * Keeps a press on the scrim beside a modal from taking focus out of it. A press on something that cannot take
 * focus leaves focus on the page, which fires no focus event the modal could bring it back from, so its controls
 * would answer no key until Tab found them again.
 *
 * @param event The press, on the scrim itself or on something inside it.
 */
export function keepFocusBeside(event: MouseEvent<HTMLDivElement>): void {
  if (event.target === event.currentTarget) {
    event.preventDefault();
  }
}

/**
 * Makes a box a modal for as long as the component that calls it is mounted.
 *
 * @param box The modal's box, which takes focus itself when it holds no control.
 * @param parts Reads the box's parts; called as the modal opens and whenever focus is brought back to it.
 * @param onClose Asks the page to take the modal away, as Escape does; the page may refuse by keeping it
 *   mounted, and the modal then keeps its layer, so the next Escape asks again.
 * @returns `trap`, the box's `onKeyDown`, which keeps Tab and Shift+Tab inside the modal while it is topmost.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function useModal(box: RefObject<HTMLElement | null>, parts: () => ModalParts, onClose: () => void): { readonly trap: (event: KeyboardEvent<HTMLElement>) => void } {
  const input = useKitInput();
  const latestClose = useRef(onClose);
  const latestParts = useRef(parts);
  const self = useRef<OpenModal | null>(null);
  const [opener] = useState(focused);
  useEffect(() => {
    latestClose.current = onClose;
    latestParts.current = parts;
  }, [onClose, parts]);
  useEffect(() => {
    const dropLayer = keepLayer(input, () => latestClose.current());
    const release = input.hold();
    const element = box.current as HTMLElement;
    const entry: OpenModal = {
      /**
       * Focuses the first safe control, or the modal itself, while the modal is on the page.
       */
      focusFirst: () => {
        if (element.isConnected) {
          (firstSafe(latestParts.current()) ?? element).focus();
        }
      },
      /**
       * Whether a node is inside the modal.
       *
       * @param node The node.
       * @returns True when the modal's box contains it.
       */
      holds: (node) => node !== null && element.contains(node),
    };
    self.current = entry;
    const leave = OPEN.open(entry);
    entry.focusFirst();
    /**
     * Brings focus that lands outside the modal back to its first safe control, while the modal is the topmost
     * one on the page.
     *
     * @param event The focus event.
     */
    const keepFocus = (event: FocusEvent): void => {
      if (OPEN.isTop(entry) && event.target instanceof Node && !entry.holds(event.target)) {
        entry.focusFirst();
      }
    };
    document.addEventListener("focusin", keepFocus);
    return () => {
      dropLayer();
      document.removeEventListener("focusin", keepFocus);
      leave();
      release();
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
      const beneath = OPEN.top();
      if (beneath !== undefined && !beneath.holds(document.activeElement)) {
        beneath.focusFirst();
      }
    };
  }, [input, opener, box]);

  /**
   * Keeps Tab and Shift+Tab inside the modal, wrapping at either end, while it is the topmost one.
   *
   * @param event The key press.
   */
  const trap = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== "Tab" || self.current === null || !OPEN.isTop(self.current) || box.current === null) {
      return;
    }
    const controls = tabbable(box.current);
    const first = controls[0];
    const last = controls.at(-1);
    if (first === undefined || last === undefined) {
      event.preventDefault();
      return;
    }
    const at = document.activeElement;
    if (event.shiftKey && (at === first || !controls.includes(at as HTMLElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (at === last || !controls.includes(at as HTMLElement))) {
      event.preventDefault();
      first.focus();
    }
  };
  return { trap };
}
